import * as React from 'react';

import FocusableContext from '../../../Bindings/React/Contexts/FocusableContext';
import FocusManager from '../../../FocusManager/FocusManager';
import FocusManagerFacade from '../../../FocusManager/FocusManagerFacade';
import * as Errors from '../../../Shared/Constants/Errors';
import ContainerContext from '../Contexts/ContainerContext';
import FocusManagerContext from '../Contexts/FocusManagerContext';
import fallbackContainerContextValue from '../Contexts/Util/fallbackContainerContextValue';

interface IFocusableProviderProps extends React.PropsWithChildren {
    /**
     * A reference to the focus manager instance.
     */

    focusManager: FocusManagerFacade;
}

/**
 * A root-level "provider" component responsible for bridging between the React world, and the
 * internal focus management business logic.
 *
 * Using a provisioned `FocusableContext`, passed down to all children, individual focusable
 * components are able to communicate with the focus manager instance in order to register and
 * unregister themselves as they are added to and removed from the react tree.
 */

const FocusableProvider: React.FC<IFocusableProviderProps> = props => {
    const {
        current: {focusManager, focusManagerFacade},
    } = React.useRef(React.useMemo(() => getFocusManagerRefs(props), []));

    /**
     * Because React components mount from leaf nodes upwards, we can infer that all active
     * focusable nodes have been registered at the point that the provider has mounted.
     */

    React.useLayoutEffect(() => {
        if (process.env.NODE_ENV !== 'production') {
            if (!focusManager.root) {
                console.warn(Errors.FOCUSABLE_CHILDREN_NOT_FOUND());
            }
        }
    }, []);

    return (
        <FocusManagerContext.Provider value={focusManagerFacade}>
            <FocusableContext.Provider
                value={{
                    changeVirtualFocus: focusManager.changeVirtualFocus,
                    config: focusManager.config,
                    focusManagerFacade,
                    handleClick: focusManager.handleClick,
                    handleNativeBlur: focusManager.handleNativeBlur,
                    handleNativeFocus: focusManager.handleNativeFocus,
                    registerFocusable: focusManager.registerFocusable,
                    reorderFocusableChildren: focusManager.reorderFocusableChildren,
                    unregisterFocusable: focusManager.unregisterFocusable,
                }}
            >
                <ContainerContext.Provider value={fallbackContainerContextValue}>
                    {React.Children.only(props.children)}
                </ContainerContext.Provider>
            </FocusableContext.Provider>
        </FocusManagerContext.Provider>
    );
};

const getFocusManagerRefs = (props: IFocusableProviderProps) => {
    if (!props.focusManager) {
        throw new Error(Errors.FOCUS_MANAGER_NOT_INJECTED());
    }

    const focusManagerFacade = props.focusManager;

    return {
        focusManagerFacade,
        // `._` member is intentionally hidden from TS to prevent public exposure
        focusManager: (focusManagerFacade as any)._ as FocusManager,
    };
};

export default FocusableProvider;
